<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';

const props = defineProps({
  catalog: { type: Object, default: () => ({}) },
  value: { type: Object, required: true },
});

const emit = defineEmits(['requestCorrection', 'update:value']);
const { t } = useI18n();
const reviewRequested = ref(false);
const showReviewConfirmation = ref(false);
const laudemios = computed(() => props.catalog.property?.laudemios || []);
const attentionPoints = computed(() => [
  t('POST_SALE.TOPICS.ATTENTION.LAUDEMIO.ENTRY'),
  t('POST_SALE.TOPICS.ATTENTION.LAUDEMIO.FORUM'),
  t('POST_SALE.TOPICS.ATTENTION.LAUDEMIO.FORUM_BILL'),
  t('POST_SALE.TOPICS.ATTENTION.LAUDEMIO.LAUDEMIO_BILL'),
  t('POST_SALE.TOPICS.ATTENTION.LAUDEMIO.ALVARA'),
]);
const typeLabels = computed(() => ({
  1: t('POST_SALE.TOPICS.LAUDEMIUM.TYPES.UNION'),
  2: t('POST_SALE.TOPICS.LAUDEMIUM.TYPES.CITY_HALL'),
  3: t('POST_SALE.TOPICS.LAUDEMIUM.TYPES.FAMILY'),
  4: t('POST_SALE.TOPICS.LAUDEMIUM.TYPES.CHURCH'),
}));
const fieldLabels = computed(() => ({
  1: t('POST_SALE.TOPICS.LAUDEMIUM.FIELDS.1'),
  3: t('POST_SALE.TOPICS.LAUDEMIUM.FIELDS.3'),
  4: t('POST_SALE.TOPICS.LAUDEMIUM.FIELDS.4'),
}));
const update = observation =>
  emit('update:value', { ...props.value, observation });
const promptReview = event => {
  if (laudemios.value.length) return;

  reviewRequested.value = event.target.checked;
  if (reviewRequested.value) showReviewConfirmation.value = true;
};
const closeReviewConfirmation = () => {
  reviewRequested.value = false;
  showReviewConfirmation.value = false;
};
const confirmReview = () => {
  closeReviewConfirmation();
  emit('requestCorrection');
};
</script>

<template>
  <div class="grid gap-4">
    <div
      class="rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
    >
      <p class="font-semibold text-lais-text">
        {{
          laudemios.length
            ? t('POST_SALE.TOPICS.LAUDEMIUM.REGISTERED')
            : t('POST_SALE.TOPICS.LAUDEMIUM.MISSING')
        }}
      </p>
      <label class="mt-3 flex items-center gap-2 text-sm text-lais-text-body">
        <input
          type="checkbox"
          :checked="laudemios.length || reviewRequested"
          :disabled="Boolean(laudemios.length)"
          @change="promptReview"
        />
        {{ t('POST_SALE.TOPICS.LAUDEMIUM.CONFIRM') }}
      </label>
      <label
        v-if="laudemios.length"
        class="mt-3 grid gap-1 text-sm font-semibold text-lais-text-body"
      >
        {{ t('POST_SALE.TOPICS.LAUDEMIUM.COUNT') }}
        <input
          :value="laudemios.length"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-2 font-normal"
          readonly
        />
      </label>
    </div>

    <div v-if="laudemios.length" class="grid gap-3">
      <div
        v-for="item in laudemios"
        :key="item.id || `${item.tipo_laudemio}-${item.valor_laudemio}`"
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-3 text-sm md:grid-cols-2"
      >
        <label class="grid gap-1 font-semibold text-lais-text">
          {{ t('POST_SALE.TOPICS.LAUDEMIUM.TYPE') }}
          <input
            :value="typeLabels[item.tipo_laudemio] || item.tipo_laudemio"
            class="min-h-10 rounded-lais border border-lais-border bg-white px-2 font-normal text-lais-text-body"
            readonly
          />
        </label>
        <label
          v-if="String(item.tipo_laudemio) !== '2'"
          class="grid gap-1 font-semibold text-lais-text"
        >
          {{ fieldLabels[item.tipo_laudemio] }}
          <input
            :value="item.valor_laudemio"
            class="min-h-10 rounded-lais border border-lais-border bg-white px-2 font-normal text-lais-text-body"
            readonly
          />
        </label>
      </div>
    </div>
    <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
      {{ t('POST_SALE.TOPICS.LAUDEMIUM.OBSERVATION') }}
      <textarea
        :value="value.observation"
        rows="3"
        class="rounded-lais border border-lais-border bg-white p-2 font-normal"
        :placeholder="t('POST_SALE.TOPICS.COMMON.WRITE_FREELY')"
        @input="update($event.target.value)"
      />
    </label>

    <div
      class="rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
    >
      <p class="text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.TOPICS.LAUDEMIUM.ATTENTION_TITLE') }}
      </p>
      <span
        class="mt-3 inline-flex rounded-full bg-lais-primary-50 px-2 py-1 text-xs font-semibold text-lais-primary"
      >
        {{ t('POST_SALE.TOPICS.ATTENTION.LAUDEMIO.TAG') }}
      </span>
      <ul class="mt-3 grid gap-2 text-sm font-semibold text-lais-text-body">
        <li v-for="point in attentionPoints" :key="point">{{ point }}</li>
      </ul>
    </div>
  </div>

  <LaisConfirmModal
    :show="showReviewConfirmation"
    :title="t('POST_SALE.TOPICS.LAUDEMIUM.REVIEW_TITLE')"
    :description="t('POST_SALE.TOPICS.LAUDEMIUM.REVIEW_DESCRIPTION')"
    :confirm-text="t('POST_SALE.TOPICS.ACTIONS.REQUEST_SALE_REVIEW')"
    :cancel-text="t('POST_SALE.TOPICS.LAUDEMIUM.REVIEW_BACK')"
    confirm-variant="primary"
    icon="i-lucide-undo-2"
    @close="closeReviewConfirmation"
    @confirm="confirmReview"
  />
</template>
